/**
 * Message Bubble Component
 *
 * Displays a single message. Layout rules:
 * - endUser  → RIGHT-aligned (lc-justify-start in RTL context), primary color
 * - bot      → LEFT-aligned (lc-justify-end in RTL), white bg + robot icon
 * - agent    → LEFT-aligned (lc-justify-end in RTL), green bg + initials + real name
 * - system   → Centered pill, no avatar
 */

import type { WidgetMessage } from '../types/widget';
import { RichText } from './RichText';

const BotIcon = ({ color }: { color: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lc-w-4 lc-h-4"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" strokeLinecap="round" />
    <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

interface MessageBubbleProps {
  message: WidgetMessage;
  primaryColor?: string;
  agentName?: string;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('he-IL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function MessageBubble({ message, primaryColor = '#FF3E6C', agentName }: MessageBubbleProps) {
  const isPending = message.status === 'pending';
  const isFailed = message.status === 'failed';

  if (message.senderType === 'system') {
    return (
      <div className="lc-flex lc-justify-center lc-w-full lc-my-1">
        <div className="lc-px-3 lc-py-1 lc-rounded-full lc-bg-gray-100 lc-text-[11px] lc-text-gray-400 lc-text-center lc-max-w-[85%]">
          {message.content}
        </div>
      </div>
    );
  }

    if (message.senderType === 'endUser') {
        return (
            <div className="lc-flex lc-justify-start lc-w-full">
                <div className="lc-max-w-[78%] lc-flex lc-flex-col">
                    <div
                        className={[
                            'lc-px-4 lc-py-2.5 lc-rounded-2xl lc-rounded-br-sm',
                            'lc-break-words lc-whitespace-pre-wrap lc-text-white',
                            isPending ? 'lc-opacity-70' : '',
                            isFailed ? 'lc-ring-1 lc-ring-red-400' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        style={{ backgroundColor: primaryColor }}
                    >
                        <div className="lc-text-sm lc-leading-relaxed">
                            <RichText text={message.content} />
                        </div>
                    </div>

                    <div className="lc-text-[10px] lc-text-gray-400 lc-mt-1 lc-px-1 lc-text-left">
                        {formatTime(message.timestamp)}
                    </div>
                </div>
            </div>
        );
    }

  if (message.senderType === 'bot') {
    return (
      <div className="lc-flex lc-justify-end lc-w-full lc-gap-2 lc-items-end">
        <div
          className="lc-w-8 lc-h-8 lc-rounded-full lc-flex lc-items-center lc-justify-center lc-flex-shrink-0 lc-mb-0.5"
          style={{ backgroundColor: `${primaryColor}20` }}
        >
          <BotIcon color={primaryColor} />
        </div>
        <div className="lc-max-w-[75%] lc-flex lc-flex-col lc-items-end">
          <div className="lc-flex lc-items-center lc-gap-1 lc-mb-1 lc-pr-1">
            {message.senderName && (
              <span className="lc-text-[11px] lc-text-gray-500">{message.senderName}</span>
            )}
            <span
              className="lc-text-[10px] lc-font-medium lc-px-1.5 lc-py-0.5 lc-rounded"
              style={{ color: primaryColor, backgroundColor: `${primaryColor}18` }}
            >
              AI
            </span>
          </div>
          <div className="lc-px-4 lc-py-2.5 lc-rounded-2xl lc-rounded-bl-sm lc-break-words lc-whitespace-pre-wrap lc-bg-white lc-shadow-sm lc-text-gray-800 lc-text-sm lc-leading-relaxed">
              <RichText text={message.content} />
          </div>
          <div className="lc-text-[10px] lc-text-gray-400 lc-mt-1 lc-pr-1">
            {formatTime(message.timestamp)}
          </div>
        </div>
      </div>
    );
  }

  if (message.senderType === 'agent') {
    const name = message.senderName || agentName || 'נציג';
    const initials = getInitials(name);

    return (
      <div className="lc-flex lc-justify-end lc-w-full lc-gap-2 lc-items-end">
        <div className="lc-w-8 lc-h-8 lc-rounded-full lc-bg-emerald-100 lc-flex lc-items-center lc-justify-center lc-text-emerald-700 lc-text-[11px] lc-font-bold lc-flex-shrink-0 lc-mb-0.5 lc-border lc-border-emerald-200">
          {initials}
        </div>
        <div className="lc-max-w-[75%] lc-flex lc-flex-col lc-items-end">
          <div className="lc-flex lc-items-center lc-gap-1 lc-mb-1 lc-pr-1">
            <span className="lc-text-[11px] lc-text-gray-500">{name}</span>
            <span className="lc-text-[10px] lc-font-medium lc-px-1.5 lc-py-0.5 lc-rounded lc-bg-emerald-50 lc-text-emerald-700">
              נציג
            </span>
          </div>
          <div className="lc-px-4 lc-py-2.5 lc-rounded-2xl lc-rounded-bl-sm lc-break-words lc-whitespace-pre-wrap lc-bg-emerald-50 lc-shadow-sm lc-text-gray-800 lc-text-sm lc-leading-relaxed lc-border lc-border-emerald-100">
            {message.content}
          </div>
          <div className="lc-text-[10px] lc-text-gray-400 lc-mt-1 lc-pr-1">
            {formatTime(message.timestamp)}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
